import {
  customerDisplayName,
  isNameUnsure,
} from "@/features/customers/name-verification";
import { UnsureNameMark } from "@/components/molecules/unsure-name-mark";
import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { AFTER_SAVE, differs, FormFooter } from "@/components/molecules/form-footer";
import { PageSkeleton } from "@/components/molecules/page-skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useCustomer,
  useUpdateCustomer,
} from "@/features/customers/hooks/use-customers";
import type { Customer } from "@/features/customers/types";
import { useTranslation } from "@/features/utils/i18n";
import { AddressBookIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PageLayout } from "../layout";
import { CustomerForm } from "./components/customer-form";

export function ContactsEditPage() {
  const { t } = useTranslation("customers");
  const { company: clientId, contactId } = useParams<{
    company: string;
    contactId: string;
  }>();
  const navigate = useNavigate();
  const {
    data: customerAndCallback,
    isLoading,
    isError,
    refetch,
  } = useCustomer(
    clientId || "",
    contactId || "",
  );
  const data = customerAndCallback?.customer;
  const updateCustomer = useUpdateCustomer();

  const [customerData, setCustomerData] = useState<Partial<Customer>>({});

  useEffect(() => {
    if (data) {
      setCustomerData(data);
    }
  }, [data]);

  const handleChange = (updates: Partial<Customer>) => {
    setCustomerData((prev) => ({ ...prev, ...updates }));
  };

  const handleSubmit = async () => {
    if (!clientId || !contactId) return;

    try {
      // The form has no display_name field, so it follows the given and family names
      // whenever they were edited — or a name struck out here would live on in it (and
      // in the list, which falls back to it). Untouched, it is kept: an imported contact
      // may carry nothing else.
      const typedName =
        `${customerData.given_name || ""} ${customerData.family_name || ""}`.trim();
      const nameEdited =
        (customerData.given_name || "") !== (data?.given_name || "") ||
        (customerData.family_name || "") !== (data?.family_name || "");
      const display_name = nameEdited
        ? typedName
        : customerData.display_name || typedName;

      await updateCustomer.mutateAsync({
        clientId,
        customerId: contactId,
        data: {
          ...customerData,
          display_name,
        },
      });

      toast.success(t("messages.updateSuccess"));
      navigate(`/${clientId}/contacts/${contactId}`, AFTER_SAVE);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.updateError"),
      );
    }
  };

  const backTo = {
    label: t("actions.back"),
    path: `/${clientId}/contacts/${contactId}`,
  };

  if (isLoading) {
    return <PageSkeleton width="narrow" blocks={["h-[32rem]"]} />;
  }

  if (isError || !data) {
    return (
      <PageLayout title={t("edit.titleDefault")} backTo={backTo} width="narrow">
        {isError ? (
          <ErrorState onRetry={refetch} />
        ) : (
          <EmptyState icon={AddressBookIcon} title={t("view.notFound")} />
        )}
      </PageLayout>
    );
  }

  const customerName = customerDisplayName(data) || t("table.unnamedCustomer");

  return (
    <PageLayout
      title={t("edit.title", { name: customerName })}
      titleSuffix={isNameUnsure(data) && <UnsureNameMark />}
      description={t("edit.description")}
      backTo={backTo}
      width="narrow"
    >
      {/* Enter in a field saves: the footer's button is this form's submit button. */}
      <form
        id="customer-edit-form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <Card>
          <CardHeader>
            <CardTitle>{t("edit.customerInfo")}</CardTitle>
          </CardHeader>
          <CardContent>
            <CustomerForm
              customer={customerData as Customer}
              onChange={handleChange}
              showNotes
            />
          </CardContent>
        </Card>
      </form>

      <FormFooter
        dirty={!!customerData.id && differs(customerData, data)}
        saving={updateCustomer.isPending}
        onSave={handleSubmit}
        onCancel={() => setCustomerData(data)}
        form="customer-edit-form"
      />
    </PageLayout>
  );
}
